import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useReactToPrint } from 'react-to-print';
import { useSnackbar } from 'notistack';

import { Grid } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import Skeleton from '@material-ui/lab/Skeleton';

import { useAdminGetOrderById } from '../hooks/admin-get-order-by-id.hook';
import { useAdminUpdateOrder } from '../hooks/admin-update-order.hook';
import { useAdminGetOrderLabels } from '../hooks/admin-get-order-labels.hook';
import { LabelsData } from '../hooks/get-labels-by-date.hook';
import OrderDetailsHeader from '../components/Containers/OrderDetailsHeader.component';
import OrderCustomerCard from '../components/Containers/OrderCustomerCard.component';
import OrderDeliveryCard from '../components/Containers/OrderDeliveryCard.component';
import OrderItemsTable from '../components/Tables/OrderItemsTable.component';
import { PrintLabelsContainer } from '../components/Print/PrintLabelsContainer.component';
import { Order } from '../types/order.type';
import { ORDER_ADV_STATUSES, OrderAdvStatus } from '../types/order-adv-status.type';
import OrderInvoicingCard from '../components/Containers/OrderInvoicingCard.component';

const useStyles = makeStyles(() => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.7rem',
  },
  gridRoot: {
    '& h4': {
      opacity: 0.7,
      margin: '0.75rem 0 0.5rem 0',
    },
  },
}));

function OrderDetailsSkeleton() {
  return (
    <>
      <Skeleton variant="rect" height={92} />
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Skeleton variant="rect" height={180} />
        </Grid>
        <Grid item xs={12} md={6}>
          <Skeleton variant="rect" height={180} />
        </Grid>
      </Grid>
      <Skeleton variant="rect" height={320} />
    </>
  );
}

export default function AdminOrderDetailsView() {
  const classes = useStyles();
  const { orderId } = useParams<{ orderId: string }>();
  const { enqueueSnackbar } = useSnackbar();

  const { isLoading, order: fetchedOrder } = useAdminGetOrderById(orderId);
  const { isUpdating, updateOrder } = useAdminUpdateOrder();
  const { isLoadingLabels, getOrderLabels } = useAdminGetOrderLabels();

  const [order, setOrder] = useState<Order | null>(null);
  const [labels, setLabels] = useState<LabelsData | null>(null);

  const printRef = useRef<HTMLDivElement>(null);
  const handlePrintLabels = useReactToPrint({ content: () => printRef.current });

  useEffect(() => {
    setOrder(fetchedOrder);
  }, [fetchedOrder]);

  const handleAdvStatusChange = async (nextStatus: OrderAdvStatus): Promise<Order | null> => {
    const updated = await updateOrder(orderId, { advStatus: nextStatus });
    if (updated) setOrder(updated);
    return updated;
  };

  const printOrderLabels = async () => {
    const fetchedLabels = await getOrderLabels(orderId);

    const hasLabels = Boolean(fetchedLabels?.products?.length || fetchedLabels?.baskets?.length);
    if (!hasLabels) {
      enqueueSnackbar('Aucune étiquette à imprimer pour cette commande', { variant: 'info' });
      return;
    }

    setLabels(fetchedLabels);
    setTimeout(() => handlePrintLabels(), 100);
  };

  const handleArchiveAndPrint = async () => {
    const updated = await handleAdvStatusChange(ORDER_ADV_STATUSES.ARCHIVED);
    if (updated) await printOrderLabels();
  };

  return (
    <div className={classes.root}>
      {isLoading || !order ? (
        <OrderDetailsSkeleton />
      ) : (
        <>
          <OrderDetailsHeader
            order={order}
            isMutating={isUpdating || isLoadingLabels}
            onAdvStatusChange={handleAdvStatusChange}
            onPrintLabels={printOrderLabels}
            onArchiveAndPrint={handleArchiveAndPrint}
          />

          <Grid className={classes.gridRoot} container spacing={2}>
            <Grid item xs={12} md={4}>
              <OrderCustomerCard order={order} />
            </Grid>
            <Grid item xs={12} md={4}>
              <OrderDeliveryCard order={order} />
            </Grid>
            <Grid item xs={12} md={4}>
              <OrderInvoicingCard order={order} />
            </Grid>
            <Grid item md={12}>
              <OrderItemsTable order={order} />
            </Grid>
          </Grid>

          <PrintLabelsContainer ref={printRef} labels={labels} />
        </>
      )}
    </div>
  );
}
